import { ReactNode } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { MagnumProvider, useMagnum } from '@/context/MagnumContext'
import MagnumCompanion from '@/components/MagnumCompanion'
import GoalProgressBar from '@/components/GoalProgressBar'
import ExerciseFrame from '@/components/ExerciseFrame'
import { cn } from '@/lib/utils'

function IdleWrapper({ children }: { children: ReactNode }) {
  const { isFrameIdle } = useMagnum()
  return (
    <div className={cn(
      'space-y-4 transition-all duration-500',
      isFrameIdle && 'opacity-20 grayscale pointer-events-none select-none',
    )}>
      {children}
    </div>
  )
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const history = useHistory()

  const handleLogout = () => {
    logout()
    history.push('/')
  }

  const isChild = user?.type === 'child'

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl font-bold text-primary tracking-tight">
            <span className="text-gold">S</span>creen <span className="text-gold">T</span>im
          </Link>

          {user && (
            <div className="flex items-center gap-4">
              {user.type === 'parent' && (
                <nav className="flex items-center gap-4 text-sm">
                  <Link to="/dashboard" className="text-foreground hover:text-primary transition">
                    Dashboard
                  </Link>
                  <Link to="/settings" className="text-foreground hover:text-primary transition">
                    Settings
                  </Link>
                  {user.parent.isAdmin && (
                    <Link to="/admin" className="px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 transition">
                      Admin
                    </Link>
                  )}
                </nav>
              )}

              {user.type === 'child' && (
                <span className="text-sm text-muted-foreground">
                  {user.child.displayName}
                </span>
              )}

              <button
                onClick={handleLogout}
                className="text-sm text-muted-foreground hover:text-foreground transition"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </header>

      {isChild ? (
        <MagnumProvider>
          <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
            <MagnumCompanion />
            <IdleWrapper>
              <GoalProgressBar />
              <ExerciseFrame>
                {children}
              </ExerciseFrame>
            </IdleWrapper>
          </main>
        </MagnumProvider>
      ) : (
        // Parent screens have no shell companion, but they still need the
        // provider: it is what stops two MagnumSays on one page from each
        // drawing Tim's face.
        <MagnumProvider>
          <main>{children}</main>
        </MagnumProvider>
      )}
    </div>
  )
}
